import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import api from "../../../services/api";
import { buildImageUrl } from "../../../config/config";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import Modal from '../../../components/Modal/Modal';
import '../../../styles/challengerequests.css';
import ClaimBusiness from "./ClaimBusiness"; // ClaimBusiness कंपोनेंट इम्पोर्ट करें

function ProviderDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [providerDetails, setProviderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);

    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    useEffect(() => {
        const fetchProviderDetails = async () => {
            if (!id) {
                setError("Provider ID is missing");
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                const response = await api.get(`/providers/${id}`);
                
                console.log("Full API Response:", response);
                console.log("Response Data:", response.data);
                
                // Handle different response structures
                if (response.data && response.data.success !== false) {
                    // Admin route returns { success: true, item: {...} }
                    const providerData = response.data.item || response.data.provider || response.data;
                    
                    console.log("Provider Data Extracted:", providerData);
                    
                    if (providerData && (providerData._id || providerData.id || providerData.name)) {
                        setProviderDetails(providerData);
                        setError(null);
                    } else {
                        console.error("Provider data structure unexpected:", providerData);
                        setError("Provider data format is invalid. Please check console for details.");
                    }
                } else {
                    const errorMsg = response.data?.message || "Provider not found";
                    console.error("API returned error:", errorMsg);
                    setError(errorMsg);
                }
            } catch (err) {
                console.error("Error fetching provider details:", err);
                console.error("Error details:", {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status
                });
                
                if (err.response?.status === 404) {
                    setError("Provider not found");
                } else if (err.response?.status === 401) {
                    setError("Unauthorized. Please log in again.");
                } else {
                    setError(err.response?.data?.message || err.message || "Failed to fetch provider details");
                }
            } finally {
                setLoading(false);
            }
        };
        
        fetchProviderDetails();
    }, [id]);

    const handleDelete = async () => {
        setIsDeleting(true);
        setShowDeleteModal(false);
        try {
            const res = await api.delete(`/providers/${id}`);
            console.log("Delete success:", res.data);
            setSuccessMessage("Provider soft deleted successfully. It will be hidden from admin panel but remain visible to users.");
            setErrorMessage("");
            setTimeout(() => {
                navigate("/admin/providers");
            }, 2000);
        } catch (err) {
            console.error("Delete failed:", err.response?.data || err.message);
            setErrorMessage(err.response?.data?.message || "Failed to delete provider.");
            setSuccessMessage("");
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) return (
        <div className="content-wrapper" style={{ marginLeft: "70px", transition: "margin-left .3s" }}>
            <div className="text-center py-5">Loading provider details...</div>
        </div>
    );
    if (error && !providerDetails) return (
        <div className="content-wrapper" style={{ marginLeft: "70px", transition: "margin-left .3s" }}>
            <div className="alert alert-danger text-center mx-3 my-3">Error: {error}</div>
        </div>
    );

    if (!providerDetails) {
        return (
            <div className="content-wrapper" style={{ marginLeft: "70px", transition: "margin-left .3s" }}>
                <div className="text-center py-5">No provider data available.</div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Admin | View Provider Details</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        {successMessage && (
                            <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ margin: "15px", backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" }}>
                                <strong>Success!</strong> {successMessage}
                                <button type="button" className="btn-close" onClick={() => setSuccessMessage("")}></button>
                            </div>
                        )}
                        {errorMessage && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ margin: "15px", backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" }}>
                                <strong>Error!</strong> {errorMessage}
                                <button type="button" className="btn-close" onClick={() => setErrorMessage("")}></button>
                            </div>
                        )}
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <Link className="breadcrumb-link" to="/admin/providers">Providers</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">Details</div>
                            </div>
                        </div>
                        <div className='container-fluid'>
                            <div className='row mt-3'>
                                <div className='col-md-12'>
                                    <div className='card'>
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <div>Provider Details</div>
                                            <div className="d-flex align-items-center">
                                                {!providerDetails.isClaimed && (
                                                    <button className="btn btn-warning btn-sm" onClick={() => setShowClaimModal(true)}>
                                                        Claim My Business
                                                    </button>
                                                )}
                                                <Link to={`/admin/providers/edit/${id}`} className="btn btn-primary btn-sm ms-2">
                                                    Edit Provider
                                                </Link>
                                            </div>
                                        </div>
                                        <div className='card-body'>
                                            <div className='table-responsive'>
                                                <table className="table table-bordered dataTable no-footer overflow-x-auto">
                                                    <tbody>
                                                        <tr>
                                                            <td style={{ width: "30%", fontWeight: "bold" }}>Name:</td>
                                                            <td>{providerDetails.name || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>URL:</td>
                                                            <td>
                                                                {providerDetails.url ? (
                                                                    <a href={providerDetails.url} target="_blank" rel="noopener noreferrer">
                                                                        {providerDetails.url}
                                                                    </a>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Website:</td>
                                                            <td>{providerDetails.website ? <a href={providerDetails.website} target="_blank" rel="noopener noreferrer">{providerDetails.website}</a> : "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Location:</td>
                                                            <td>
                                                                {providerDetails.location ? (
                                                                    typeof providerDetails.location === 'object' 
                                                                        ? (providerDetails.location.name || providerDetails.location._id || "N/A")
                                                                        : providerDetails.location
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Address:</td>
                                                            <td>{providerDetails.address || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Postal Code:</td>
                                                            <td>{providerDetails.postalCode || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Contact Number:</td>
                                                            <td>{providerDetails.contactNumber || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Number of Employees:</td>
                                                            <td>{providerDetails.numberOfEmployees || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Founded Year:</td>
                                                            <td>{providerDetails.foundedYear || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Short Description:</td>
                                                            <td>{providerDetails.shortDescription || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Rating:</td>
                                                            <td>{providerDetails.rating || 0}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Total Reviews:</td>
                                                            <td>{providerDetails.totalReviews || 0}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Price Tier:</td>
                                                            <td>{providerDetails.priceTier || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Is Verified:</td>
                                                            <td>{providerDetails.isVerified ? "Yes" : "No"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Is Active:</td>
                                                            <td>{providerDetails.isActive ? "Yes" : "No"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Cover Image:</td>
                                                            <td>
                                                                {providerDetails.coverImage ? (
                                                                    <img src={buildImageUrl(providerDetails.coverImage)} alt="Cover" style={{ maxWidth: "200px", maxHeight: "200px" }} />
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Icon:</td>
                                                            <td>
                                                                {providerDetails.icon ? (
                                                                    <img src={buildImageUrl(providerDetails.icon)} alt="Icon" style={{ maxWidth: "100px", maxHeight: "100px" }} />
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Highlights:</td>
                                                            <td>
                                                                {providerDetails.highlights && Array.isArray(providerDetails.highlights) && providerDetails.highlights.length > 0 ? (
                                                                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                                                        {providerDetails.highlights.map((highlight, idx) => (
                                                                            <li key={idx || idx}>
                                                                                {typeof highlight === 'object' 
                                                                                    ? (highlight.name || highlight._id || "N/A")
                                                                                    : (highlight || "N/A")
                                                                                }
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Features:</td>
                                                            <td>
                                                                {providerDetails.features && Array.isArray(providerDetails.features) && providerDetails.features.length > 0 ? (
                                                                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                                                        {providerDetails.features.map((feature, idx) => (
                                                                            <li key={idx || idx}>
                                                                                {typeof feature === 'object' 
                                                                                    ? (feature.name || feature._id || "N/A")
                                                                                    : (feature || "N/A")
                                                                                }
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Fabrics:</td>
                                                            <td>
                                                                {providerDetails.fabrics && Array.isArray(providerDetails.fabrics) && providerDetails.fabrics.length > 0 ? (
                                                                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                                                        {providerDetails.fabrics.map((fabric, idx) => (
                                                                            <li key={idx || idx}>
                                                                                {typeof fabric === 'object' 
                                                                                    ? (fabric.name || fabric._id || "N/A")
                                                                                    : (fabric || "N/A")
                                                                                }
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Categories:</td>
                                                            <td>
                                                                {providerDetails.categories && Array.isArray(providerDetails.categories) && providerDetails.categories.length > 0 ? (
                                                                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                                                        {providerDetails.categories.map((category, idx) => (
                                                                            <li key={idx || idx}>
                                                                                {typeof category === 'object' 
                                                                                    ? (category.name || category._id || "N/A")
                                                                                    : (category || "N/A")
                                                                                }
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                        {providerDetails.socialMedia && providerDetails.socialMedia.length > 0 && (
                                                            <tr>
                                                                <td style={{ fontWeight: "bold" }}>Social Media:</td>
                                                                <td>
                                                                    <ul>
                                                                        {providerDetails.socialMedia.map((social, idx) => (
                                                                            <li key={idx}>{social.platform}: <a href={social.url} target="_blank" rel="noopener noreferrer">{social.url}</a></li>
                                                                        ))}
                                                                    </ul>
                                                                </td>
                                                            </tr>
                                                        )}
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Created At:</td>
                                                            <td>{providerDetails.createdAt ? new Date(providerDetails.createdAt).toLocaleString() : "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ fontWeight: "bold" }}>Updated At:</td>
                                                            <td>{providerDetails.updatedAt ? new Date(providerDetails.updatedAt).toLocaleString() : "-"}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="mt-3">
                                                <button 
                                                    className="btn btn-danger" 
                                                    onClick={() => setShowDeleteModal(true)}
                                                    disabled={isDeleting}
                                                >
                                                    Delete Provider
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Provider"
                message="Are you sure you want to soft delete this provider? It will be hidden from the admin panel but remain visible to users."
                confirmText="Delete"
                cancelText="Cancel"
            />
            {!providerDetails.isClaimed && (
                <ClaimBusiness
                    show={showClaimModal}
                    onHide={() => setShowClaimModal(false)}
                    businessId={id}
                    onClaimSuccess={(msg) => {
                        setSuccessMessage(msg);
                        // Modal will stay open for OTP entry
                    }}
                    onVerificationSuccess={(msg) => setSuccessMessage(msg)}
                />
            )}
        </>
    );
}

export default ProviderDetails;
